import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import AqiDisplay from "../components/AqiDisplay";
import NoAccessPage from "./NoAccessPage";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import CustomDateInputs from "../components/CustomDateInputs";
import useJWTInterceptors from "../authentication/useJWTInterceptors";
import {
  formatDateAndTimeHourly,
  formatDateAndTimeOnline,
  formatDateDaily,
} from "../components/DateAndTimeFunctions";

const StationFullDataPaginated = () => {
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const { theRouteUrl } = useParams();
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;
  const [sensorType, setSensorType] = useState("pollution");
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );
  const [currentPageData, setCurrentPageData] = useState([]);
  const [device, setDevice] = useState();
  const [deviceID, setDeviceID] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [count, setCount] = useState(0);
  const [selectedOption, setSelectedOption] = useState("hourly");
  const [isOPM, setIsOpm] = useState();
  const [isKit, setIsKit] = useState();
  const [pageIsValid, setPageIsValid] = useState(true);
  const tokenStr = process.env.REACT_APP_API_TOKEN;
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [selectedAqiType, setSelectedAqiType] = useState("hourly");
  const [selectedSensorType, setSelectedSensorType] = useState("pollution");

  const theSpecificStation = useMemo(
    () =>
      Array.isArray(informationData)
        ? informationData.find((x) => x.slug === theRouteUrl)
        : null,
    [informationData, theRouteUrl, informationLoading, informationError]
  );

  useEffect(() => {
    if (Array.isArray(informationData) && informationData.length > 0) {
      setPageIsValid(!!theSpecificStation);
      setIsOpm(theSpecificStation.is_opm);
      setIsKit(theSpecificStation.is_kit);
      setDevice(theSpecificStation.device);
      setDeviceID(theSpecificStation.device_id);
    }
  }, [theSpecificStation, informationData]);

  const historyApiUrl = useMemo(() => {
    if (!device || !deviceID || !baseURL) return "";

    let startDate, endDate, granularity;

    if (sensorType === "pollution") {
      let baseEndpoint = `${baseURL}history/${device}/${deviceID}/`;

      switch (selectedOption) {
        case "online":
          startDate = formatDateAndTimeOnline(selectedStartDateAndTime);
          endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
          granularity = 0;
          break;
        case "hourly":
          startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
          endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
          granularity = 1;
          break;
        case "daily":
          startDate = formatDateDaily(selectedStartDateAndTime);
          endDate = formatDateDaily(selectedEndDateAndTime);
          granularity = 2;
          break;
        case "twelve":
          startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
          endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
          granularity = 3;
          break;
        case "twentyFour":
          startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
          endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
          granularity = 4;
          break;
        default:
          return "";
      }

      return `${baseEndpoint}${granularity}?start_date=${startDate}&end_date=${endDate}`;
    }

    if (sensorType === "environment") {
      startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
      endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
      granularity = 1;
      let baseEndpoint = `${baseURL}history/KIT/${deviceID}/`;
      return `${baseEndpoint}${granularity}?start_date=${startDate}&end_date=${endDate}&device_type=${
        device === "SAPA" ? 1 : 0
      }`;
    }

    return "";
  }, [
    device,
    deviceID,
    baseURL,
    selectedOption,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
    sensorType,
  ]);

  const excelApiUrl = useMemo(() => {
    if (!device || !deviceID || !baseURL) return "";

    let startDate, endDate, granularity;

    if (sensorType === "pollution") {
      let excelBaseEndpoint = `${baseURL}${device.toLowerCase()}_excel/?device_id=${deviceID}`;

      switch (selectedOption) {
        case "online":
          startDate = formatDateAndTimeOnline(selectedStartDateAndTime);
          endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
          granularity = 0;
          break;
        case "hourly":
          startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
          endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
          granularity = 1;
          break;
        case "daily":
          startDate = formatDateDaily(selectedStartDateAndTime);
          endDate = formatDateDaily(selectedEndDateAndTime);
          granularity = 2;
          break;
        case "twelve":
          startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
          endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
          granularity = 3;
          break;
        case "twentyFour":
          startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
          endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
          granularity = 4;
          break;
        default:
          return "";
      }

      return `${excelBaseEndpoint}&start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`;
    }

    if (sensorType === "environment") {
      startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
      endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
      granularity = 1;
      let kitBaseEndpoint = `${baseURL}kit_excel/?device_id=${deviceID}`;
      return `${kitBaseEndpoint}&device_type=${
        device === "SAPA" ? 1 : 0
      }&granularity=${granularity}&start_date=${startDate}&end_date=${endDate}`;
    }

    return "";
  }, [
    device,
    deviceID,
    baseURL,
    selectedOption,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
    sensorType,
  ]);

  const getHistoryData = async () => {
    setSelectedAqiType(selectedOption);
    setSelectedSensorType(sensorType);
    setLoading(true);
    try {
      const response = await useJWTInterceptors.get(
        `${historyApiUrl}&page=${pageNumber}`
      );
      setCurrentPageData(response.data.results);
      setTotalDataCount(response.data.count || 0);
      setTotalPageNumber(Math.ceil((response.data.count || 0) / 50));
      setError(false);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch latest data only when `count` or `pageNumber` changes
  useEffect(() => {
    if (!historyApiUrl) return; // Ensure API URL is ready

    getHistoryData();
  }, [count, pageNumber]); // Fetch only when `count` or `pageNumber` changes

  const downloadExcelReport = async () => {
    setExcelLoading(true);

    try {
      // Make a GET request to the server to fetch the Excel file
      const response = await useJWTInterceptors.get(excelApiUrl, {
        responseType: "blob", // set responseType to 'blob' for binary data
      });

      // Create a blob from the binary data received in the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a URL for the blob and use it to create a link element
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Set the link's properties for download
      link.href = url;
      link.download = `${formatDateAndTimeOnline(
        selectedStartDateAndTime
      )}_${formatDateAndTimeOnline(selectedEndDateAndTime)}.xlsx`; // specify the desired file name

      // Append the link to the document body and trigger a click to start download
      document.body.appendChild(link);
      link.click();

      // Clean up the created elements after the download is initiated
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error("Error downloading Excel report:", error);
      setExcelError(error);
    } finally {
      setExcelLoading(false);
    }
    // console.log("excelLoading:", excelLoading);
  };

  // Utility function for date formatting
  const convertToJalali = useCallback((x) => {
    if (x) {
      const A = x.slice(0, 10);
      const B = x.slice(11, 19);
      const C = new Date(A);
      const D = new Intl.DateTimeFormat("fa-IR").format(C);
      const E = D.concat(" ", "(", B, ")");
      return E;
    }
  }, []);

  // Render conditionally based on whether the page is valid
  if (!isLoggedIn) {
    return <NoAccessPage />;
  }
  if (isLoggedIn) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="air"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            setCount={setCount}
            count={count}
            excelLoading={excelLoading}
            downloadExcelReport={downloadExcelReport}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            isKit={isKit}
            setSensorType={setSensorType}
            sensorType={sensorType}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {error || loading || currentPageData.length < 1 ? (
                ""
              ) : (
                <PaginationButtonToolbar
                  type="air"
                  setSelectedStartDateAndTime
                  selectedOption={selectedOption}
                  pageNumber={pageNumber}
                  setPageNumber={setPageNumber}
                  totalPageNumber={totalPageNumber}
                />
              )}
            </div>
            {informationLoading ||
            informationError ||
            error ||
            loading ||
            (Array.isArray(currentPageData) &&
              currentPageData.length < 1) ? null : Array.isArray(
                currentPageData
              ) &&
              currentPageData?.length > 0 &&
              selectedSensorType === "pollution" ? (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      <th className="IRANSansXE p-3">شاخص AQI</th>

                      <th className="IRANSansXE p-3">آلاینده عامل</th>

                      {/* {device === "SATA" || device === "SAPA" ? (
                        <>
                          {deviceID === 131 &&  deviceID === 131 ? (
                            ""
                          ) : (
                            <>
                              <th className="IRANSansXE p-3">CO</th>
                              <th className="IRANSansXE p-3">O3</th>
                              <th className="IRANSansXE p-3">
                                NO<sub>2</sub>
                              </th>
                              <th className="IRANSansXE p-3">
                                SO<sub>2</sub>
                              </th>
                            </>
                          )}
                        </>
                      ) : (
                        ""
                      )} */}

                      {device === "SATA" || device === "SAPA" ? (
                        <>
                          <th className="IRANSansXE p-3">CO</th>
                          <th className="IRANSansXE p-3">O3</th>
                          <th className="IRANSansXE p-3">
                            NO<sub>2</sub>
                          </th>
                          <th className="IRANSansXE p-3">
                            SO<sub>2</sub>
                          </th>
                        </>
                      ) : null}

                      <th className="IRANSansXE p-3">PM2.5</th>

                      <th className="IRANSansXE p-3">PM10</th>

                      {selectedAqiType === "hourly" ||
                      selectedAqiType === "online" ? (
                        <>
                          {device === "OSPM" && deviceID === 127 ? (
                            ""
                          ) : (
                            <th className="IRANSansXE p-3">دما</th>
                          )}

                          {device === "SATA" ? (
                            <th className="IRANSansXE p-3">فشار</th>
                          ) : (
                            ""
                          )}

                          <th className="IRANSansXE p-3">رطوبت</th>
                          {device === "SATA" ? (
                            <>
                              <th className="IRANSansXE p-3">صوت</th>
                              <th className="IRANSansXE p-3">بو</th>
                              <th className="IRANSansXE p-3">جهت باد</th>
                              <th className="IRANSansXE p-3">سرعت باد</th>
                            </>
                          ) : (
                            ""
                          )}
                        </>
                      ) : (
                        ""
                      )}

                      <th className="IRANSansXE p-3">تاریخ و ساعت</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentPageData.map((station) => (
                      <tr
                        key={
                          station.ccreated_datetime || station.created_datetime
                        }
                      >
                        {station.AQI === null ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td className="text-center px-md-3 align-baseline">
                            <AqiDisplay
                              AQI={Number(station.AQI)?.toFixed(
                                device === "SATA" ? 1 : 2
                              )}
                            />
                          </td>
                        )}

                        {station.mainPollutant === null ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            {station.mainPollutant}
                          </td>
                        )}

                        {device === "SATA" || device === "SAPA" ? (
                          <>
                            {/* {deviceID === 131  || deviceID === 132? (
                              ""
                            ) : ( */}
                            <>
                              {station.co === null ||
                              station.co === undefined ? (
                                <td
                                  className="text-center px-md-5 align-baseline"
                                  dir="ltr"
                                >
                                  -
                                </td>
                              ) : (
                                <td className="IRANSansXB">
                                  <p>
                                    {Number(station.co).toFixed(
                                      isOPM || device === "SATA" ? 1 : 2
                                    )}
                                    <sub>
                                      {device === "SATA" ? "ppm" : "ppb"}
                                    </sub>
                                  </p>
                                </td>
                              )}

                              {station.o3 !== null || station.o3 === 0 ? (
                                <td className="IRANSansXB">
                                  <p>
                                    {Number(station.o3).toFixed(
                                      isOPM ? 1 : device === "SATA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td
                                  className="text-center px-md-5 align-baseline"
                                  dir="ltr"
                                >
                                  -
                                </td>
                              )}

                              {station.no2 !== null || station.no2 === 0 ? (
                                <td className="IRANSansXB">
                                  <p>
                                    {Number(station.no2).toFixed(
                                      isOPM ? 1 : device === "SATA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td
                                  className="text-center px-md-5 align-baseline"
                                  dir="ltr"
                                >
                                  -
                                </td>
                              )}

                              {station.so2 !== null || station.so2 === 0 ? (
                                <td className="IRANSansXB">
                                  <p>
                                    {Number(station.so2).toFixed(
                                      isOPM ? 1 : device === "SATA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td
                                  className="text-center px-md-5 align-baseline"
                                  dir="ltr"
                                >
                                  -
                                </td>
                              )}
                            </>
                            {/* )} */}
                          </>
                        ) : (
                          ""
                        )}

                        {station.pm25 === null ||
                        station.pm25 === undefined ||
                        (!station.temperature &&
                          !station.humidity &&
                          !station.co &&
                          !station.o3 &&
                          !station.AQI) ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td>
                            <p>
                              {Number(station.pm25).toFixed(
                                isOPM || device === "SATA" ? 1 : 2
                              )}
                              <sub>
                                μg/m<sup>3</sup>
                              </sub>
                            </p>
                          </td>
                        )}

                        {station.pm10 === null ||
                        station.pm10 === undefined ||
                        (!station.temperature &&
                          !station.humidity &&
                          !station.co &&
                          !station.o3 &&
                          !station.AQI) ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td>
                            <p>
                              {Number(station.pm10).toFixed(
                                isOPM || device === "SATA" ? 1 : 2
                              )}
                              <sub>
                                μg/m<sup>3</sup>
                              </sub>
                            </p>
                          </td>
                        )}

                        {selectedAqiType === "hourly" ||
                        selectedAqiType === "online" ? (
                          <>
                            {station.temperature === null ||
                            station.temperature === undefined ? (
                              <td
                                className="text-center px-md-5 align-baseline"
                                dir="ltr"
                              >
                                -
                              </td>
                            ) : (
                              <td className="IRANSansXB">
                                <p>
                                  <sub>℃</sub>
                                  {Number(station.temperature).toFixed(
                                    device === "SATA" ? 1 : 2
                                  )}
                                </p>
                              </td>
                            )}
                            {device === "SATA" ? (
                              <>
                                {station.pressure === null ||
                                station.pressure === undefined ? (
                                  <td
                                    className="text-center px-md-5 align-baseline"
                                    dir="ltr"
                                  >
                                    -
                                  </td>
                                ) : (
                                  <td className="IRANSansXB">
                                    <p>
                                      <sub>kPa</sub>
                                      {Number(station.temperature).toFixed(
                                        device === "SATA" ? 1 : 2
                                      )}
                                    </p>
                                  </td>
                                )}

                                {station.humidity == null ||
                                station.pm25 == null ? (
                                  <td
                                    className="text-center px-md-5 align-baseline"
                                    dir="ltr"
                                  >
                                    -
                                  </td>
                                ) : (
                                  <td className="IRANSansXB">
                                    <p>
                                      {" "}
                                      <sub>RH%</sub>
                                      {Number(station.humidity).toFixed(
                                        device === "SATA" ? 1 : 2
                                      )}
                                    </p>
                                  </td>
                                )}
                              </>
                            ) : (
                              ""
                            )}

                            {station.humidity === null ||
                            station.humidity === undefined ? (
                              <td
                                className="text-center px-md-5 align-baseline"
                                dir="ltr"
                              >
                                -
                              </td>
                            ) : (
                              <td className="IRANSansXB">
                                <p>
                                  <sub>RH%</sub>
                                  {Number(station.humidity).toFixed(
                                    device === "SATA" ? 1 : 2
                                  )}
                                </p>
                              </td>
                            )}

                            {device === "SATA" ? (
                              <>
                                {station.spl === null ||
                                station.spl === undefined ? (
                                  <td
                                    className="text-center px-md-5 align-baseline"
                                    dir="ltr"
                                  >
                                    -
                                  </td>
                                ) : (
                                  <td className="IRANSansXB">
                                    <p>
                                      <sub>dB</sub>
                                      {Number(station.spl).toFixed(2)}
                                    </p>
                                  </td>
                                )}

                                {station.tvoc === null ||
                                station.tvoc === undefined ? (
                                  <td
                                    className="text-center px-md-5 align-baseline"
                                    dir="ltr"
                                  >
                                    -
                                  </td>
                                ) : (
                                  <td className="IRANSansXB">
                                    <p>
                                      <sub>ppm</sub>
                                      {Number(station.tvoc).toFixed(
                                        device === "SATA" ? 0 : 2
                                      )}
                                    </p>
                                  </td>
                                )}

                                {station.winddirection === null ||
                                station.winddirection === undefined ? (
                                  <td
                                    className="text-center px-md-5 align-baseline"
                                    dir="ltr"
                                  >
                                    -
                                  </td>
                                ) : (
                                  <td className="IRANSansXB">
                                    <p>
                                      {Number(station.winddirection).toFixed(0)}
                                      <sup>°</sup>
                                    </p>
                                  </td>
                                )}

                                {station.windspeed === null ||
                                station.windspeed === undefined ? (
                                  <td
                                    className="text-center px-md-5 align-baseline"
                                    dir="ltr"
                                  >
                                    -
                                  </td>
                                ) : (
                                  <td className="IRANSansXB">
                                    <p>
                                      <sub>m/s</sub>
                                      {Number(station.windspeed).toFixed(0)}
                                    </p>
                                  </td>
                                )}
                              </>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          ""
                        )}

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (Array.isArray(currentPageData) && currentPageData.length) >
                0 && selectedSensorType === "environment" ? (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      <th className="IRANSansXE p-3">فشار</th>

                      <th className="IRANSansXE p-3">SPL</th>

                      <th className="IRANSansXE p-3">TVOC</th>

                      <th className="IRANSansXE p-3">تاریخ و ساعت</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentPageData.map((station) => (
                      <tr
                        key={
                          station.ccreated_datetime || station.created_datetime
                        }
                      >
                        {station.pressure === null ||
                        station.pressure === undefined ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td
                            className="text-center px-md-3 align-baseline"
                            dir="ltr"
                          >
                            {Number(station.pressure)?.toFixed(2)}
                            <sub> kPa</sub>
                          </td>
                        )}

                        {station.spl === null || station.spl === undefined ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            {Number(station.spl)?.toFixed(2)}
                            <sub> dB</sub>
                          </td>
                        )}

                        {station.voc === null || station.voc === undefined ? (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            -
                          </td>
                        ) : (
                          <td
                            className="text-center px-md-5 align-baseline"
                            dir="ltr"
                          >
                            {Number(station.voc)?.toFixed(2)}
                            <sub> ppb</sub>
                          </td>
                        )}

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
};

export default StationFullDataPaginated;
